import React, { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { 
  Layers, Film, Video, Zap, Code, 
  Smartphone, Monitor, Share2, TrendingUp, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";

const allServices = [
  {
    title: "Branding",
    icon: <Layers className="w-8 h-8" />,
    href: "/services/branding",
    desc: "Visual identities that tell your unique story through cinematic design.",
    video: "https://videos.pexels.com/video-files/7598768/7598768-hd_1920_1080_30fps.mp4",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Film Making",
    icon: <Film className="w-8 h-8" />,
    href: "/services/film-making",
    desc: "High-end cinematic productions that capture the essence of your vision.",
    video: "https://videos.pexels.com/video-files/34550102/14640321_1440_2560_50fps.mp4",
    image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Video Editing",
    icon: <Video className="w-8 h-8" />,
    href: "/services/video-editing",
    desc: "Precision post-production that transforms raw footage into compelling stories.",
    video: "https://videos.pexels.com/video-files/29380116/12656384_1080_1920_30fps.mp4",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Motion Graphics",
    icon: <Zap className="w-8 h-8" />,
    href: "/services/motion-graphics",
    desc: "Dynamic visual effects and animations that bring your ideas to life.",
    video: "https://videos.pexels.com/video-files/34934668/14798533_1920_1080_30fps.mp4",
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Web Development",
    icon: <Code className="w-8 h-8" />,
    href: "/services/web-development",
    desc: "Bespoke digital engineering focused on performance and aesthetic excellence.",
    video: "https://videos.pexels.com/video-files/6804109/6804109-uhd_2732_1440_25fps.mp4",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=800"
  },

  {
    title: "UI / UX Design",
    icon: <Monitor className="w-8 h-8" />,
    href: "/services/ui-ux-design",
    desc: "Human-centric interfaces that combine beauty with effortless functionality.",
    video: "https://videos.pexels.com/video-files/8051636/8051636-uhd_1440_2560_24fps.mp4",
    image: "https://images.unsplash.com/photo-1545235617-946f72a53d99?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Social Media",
    icon: <Share2 className="w-8 h-8" />,
    href: "/services/social-media",
    desc: "Strategic content creation and management that builds online authority.",
    video: "https://videos.pexels.com/video-files/34953967/14806517_1920_1080_50fps.mp4",
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=800"
  },
  {
    title: "Performance Marketing",
    icon: <TrendingUp className="w-8 h-8" />,
    href: "/services/performance-marketing",
    desc: "Scale your customer acquisition. High-intent Meta Ads and Google Ads strategies built for measurable ROI.",
    video: "https://videos.pexels.com/video-files/7792231/7792231-hd_1920_1080_25fps.mp4",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
  }
];

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Services — Specialized Studio Solutions" 
        description="Meraki Solutions offers a full suite of cinematic film making and digital engineering services."
      />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <Navbar />
      <main>
        <SectionStepper 
          sections={[
            { id: "top", label: "Specialized" },
            { id: "grid", label: "Solutions" },
            { id: "ethos", label: "Our Ethos" },
            { id: "cta", label: "Start Now" }
          ]} 
        />
        {/* Hero Section */}
        <section id="top" className="relative pt-44 pb-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.35 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/3130182/3130182-uhd_2560_1440_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>
          
          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              className="max-w-4xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">Our Craft</span>
              <h1 className="font-display text-6xl md:text-9xl mb-12 leading-none">
                Specialized <br /> <span className="italic gold-text">Solutions</span>
              </h1>
              <p className="text-[var(--cream-muted)] text-xl font-body leading-relaxed max-w-2xl">
                From cinematic storytelling to complex digital engineering, we provide end-to-end services tailored for premium brands and visionary founders.
              </p>
            </motion.div>
          </div>
        </section>

      {/* Services Grid */}
      <section id="grid" className="pb-44">
        <div className="container-xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group relative h-[450px] rounded-[32px] overflow-hidden border border-[var(--rule)]"
              >
                {service.video ? (
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline 
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover grayscale opacity-40 group-hover:opacity-60 transition-opacity duration-1000"
                  >
                    <source src={service.video} type="video/mp4" />
                  </video>
                ) : (
                  <div 
                    className="absolute inset-0 transition-transform duration-1000 group-hover:scale-110 grayscale opacity-40 group-hover:opacity-60"
                    style={{ backgroundImage: `url(${service.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/40 to-transparent" />
                
                <div className="absolute inset-0 p-10 flex flex-col justify-end">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                    {service.icon}
                  </div>
                  <h3 className="font-display text-3xl mb-4 text-[var(--cream)]">{service.title}</h3>
                  <p className="text-[var(--cream-muted)] text-sm leading-relaxed mb-8 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0">
                    {service.desc}
                  </p>
                  <Link to={service.href} className="flex items-center gap-4 text-[var(--gold)] font-impact text-[10px] tracking-widest uppercase">
                    Explore Service <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ethos Section */}
      <section id="ethos" className="py-44 bg-[var(--ink)] overflow-hidden">
        <div className="container-xl px-6">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">Our Philosophy</span>
              <h2 className="font-display text-5xl md:text-8xl mb-8 leading-[1.1]">The Meraki <br /><span className="italic gold-text">Ethos</span></h2>
              <p className="text-[var(--cream-muted)] text-xl leading-relaxed mb-10 max-w-xl">
                We don't believe in templates. Every pixel, every frame, and every line of code is engineered from the ground up to reflect the unique soul of your brand. Our boutique approach ensures that quality is never sacrificed for volume.
              </p>
              <div className="flex flex-col gap-6">
                {[
                  { title: "Artisan Quality", desc: "Hand-crafted solutions that stand out in a world of generic digital products." },
                  { title: "Cinematic Vision", desc: "Bringing the depth and emotion of film to every digital interaction." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 p-6 rounded-2xl bg-[var(--ink-2)] border border-[var(--rule)]">
                    <div className="text-[var(--gold)] font-impact">0{i+1}</div>
                    <div>
                      <h4 className="text-[var(--cream)] font-display text-xl mb-1">{item.title}</h4>
                      <p className="text-[var(--muted)] text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative aspect-[4/5] rounded-[40px] overflow-hidden border border-[var(--rule)]"
            >
              <img 
                src="/images/studio_ethos.png" 
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                alt="Meraki Studio Ethos"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/40 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work Together CTA */}
      <section id="cta" className="py-44 bg-[var(--ink-2)] border-y border-[var(--rule)] relative overflow-hidden">
        <div className="container-xl px-6 relative z-10 text-center">
          <h2 className="font-display text-5xl md:text-8xl mb-12">Ready to <span className="italic gold-text">Begin?</span></h2>
          <Link to="/contact" className="btn-primary inline-flex">
            Start a Project <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
        {/* Animated background text */}
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] pointer-events-none select-none">
          <div className="font-display italic text-[25vw] whitespace-nowrap">CREATIVE STUDIO</div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
